import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClientEndpoints, EventTriggers, Facilities } from "@app/shared/src/api/api.gen";
import {
    ClientEndpoint,
    Device,
    EventTrigger,
    Facility,
    FleetDefaults,
    RelaySelection,
    WebRelayClientEndpoint,
} from "@app/shared/src/models";
import { Btn, Card, CardNote, CardHead, FRow, Note, Select, Spinner, Tag, TextInput, useToast } from "@app/shared/src/components_v2";
import { ALERT_CATEGORIES, ALERT_CATEGORY_LABELS, AlertCategory, triggersByCategory } from "../../helpers/alertCategories";
import { setRelayPulseSeconds, triggersForFacility } from "../../helpers/alertSettings";
import {
    NotificationSettings,
    applyEmailNotification,
    applyRelaySelection,
    endpointRecipients,
    isEmailEndpoint,
    fleetCanEmail,
    notificationDifferences,
    notificationsFromDefaults,
    notificationsMatch,
    readNotificationSettings,
    rewireRelayNumbers,
} from "../../helpers/notificationSettings";
import { RelayWiringMap, effectiveRelayWiring } from "@app/shared/src/helpers/relayWiring";
import {
    PROTECTED_ZONE_STEP,
    ensureEmailEndpoint,
    ensureRelayEndpoint,
    findProtectedZone,
    fleetRunsAlert,
    provisionMissingAlerts,
    provisionStepLabel,
    repairTrigger,
} from "../../helpers/alertProvisioning";
import { InheritedSection } from "../shared/InheritedSection";
import { ProgressDialog, useProgressSteps } from "../shared/ProgressDialog";
import { SaveBar } from "../shared/SaveBar";
import { Toggle } from "../shared/Toggle";

/**
 * Who hears about this truck's alerts, and what fires when one does.
 *
 * Neither is a column: an email is sent because a row joins the trigger to an email endpoint, and a
 * relay closes because a row joins it to a web relay whose config names the terminal. So every
 * switch here adds or removes a join, and the relay picker rewrites a JSON blob that devices parse.
 *
 * Endpoints are per location, and a location that has none simply cannot notify anyone — so the
 * recipients list says that plainly rather than showing an empty table that looks configured.
 */

/** The four slot selections, labelled with what this truck's relays are actually called. */
export const relayChoices = (wiring: RelayWiringMap): { value: RelaySelection; label: string }[] => [
    { value: 0 as RelaySelection, label: "None" },
    { value: 1 as RelaySelection, label: wiring.lights.name },
    { value: 2 as RelaySelection, label: wiring.horn.name },
    { value: 3 as RelaySelection, label: "Both" },
];

/**
 * The truck's wiring overrides as a form draft. Every field has an explicit "follow the fleet"
 * state — blank / "" — because that is the null the endpoint stores, and it is the common case.
 */
type SlotOverride = { number: string; name: string; snoozable: "" | "yes" | "no" };
type WiringOverrides = { horn: SlotOverride; lights: SlotOverride };

const overridesOf = (e: WebRelayClientEndpoint | undefined): WiringOverrides => ({
    horn: {
        number: e?.hornRelayNumber != null ? String(e.hornRelayNumber) : "",
        name: e?.hornRelayName ?? "",
        snoozable: e?.hornRelaySnoozable == null ? "" : e.hornRelaySnoozable ? "yes" : "no",
    },
    lights: {
        number: e?.lightsRelayNumber != null ? String(e.lightsRelayNumber) : "",
        name: e?.lightsRelayName ?? "",
        snoozable: e?.lightsRelaySnoozable == null ? "" : e.lightsRelaySnoozable ? "yes" : "no",
    },
});

/** The endpoint fields a draft resolves to — undefined where the truck follows the fleet. */
const overrideFieldsOf = (d: WiringOverrides) => ({
    hornRelayNumber: d.horn.number ? Number(d.horn.number) : undefined,
    hornRelayName: d.horn.name.trim() || undefined,
    hornRelaySnoozable: d.horn.snoozable === "" ? undefined : d.horn.snoozable === "yes",
    lightsRelayNumber: d.lights.number ? Number(d.lights.number) : undefined,
    lightsRelayName: d.lights.name.trim() || undefined,
    lightsRelaySnoozable: d.lights.snoozable === "" ? undefined : d.lights.snoozable === "yes",
});

export const NotificationsSection: React.FC<{
    device: Device;
    triggers?: EventTrigger[];
    defaults?: FleetDefaults;
    onSaved: () => void;
}> = ({ device, triggers, defaults, onSaved }) => {
    const toast = useToast();
    const [endpoints, setEndpoints] = useState<ClientEndpoint[]>();
    const [facility, setFacility] = useState<Facility>();
    const [busy, setBusy] = useState(false);
    const [overridden, setOverridden] = useState(false);
    // The save's progress bar. Destructured because the hook returns a fresh object each render
    // and only the callbacks are stable enough to sit in commit's dependency list.
    const { run: progressRun, begin: beginProgress, report: reportProgress, fail: failProgress, end: endProgress } =
        useProgressSteps();

    useEffect(() => {
        let cancelled = false;
        if (!device.facilityId) {
            setEndpoints([]);
            return;
        }
        ClientEndpoints.getAll(device.organizationId, device.facilityId)
            .then((e) => !cancelled && setEndpoints(e ?? []))
            .catch(() => !cancelled && setEndpoints([]));
        // Held so an endpoint can be provisioned onto the right site when one is missing.
        Facilities.getById(device.facilityId)
            .then((f) => !cancelled && setFacility(f))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [device.organizationId, device.facilityId]);

    const byCategory = useMemo(() => triggersByCategory(triggers ?? []), [triggers]);
    const emailEndpoints = useMemo(
        () => (endpoints ?? []).filter((e) => isEmailEndpoint(e as { $type?: string })),
        [endpoints],
    );

    // This truck's effective wiring — which terminal is the horn HERE — from its own relay
    // endpoint's overrides over the fleet default. Every read and write below goes through it.
    const relayEndpoint = useMemo(
        () => (endpoints ?? []).find((e) => (e as { $type?: string }).$type?.includes("WebRelay")),
        [endpoints],
    );
    const wiring = useMemo(() => effectiveRelayWiring(defaults, relayEndpoint), [defaults, relayEndpoint]);

    // The wiring override draft, kept apart from the notification draft: it saves to a different
    // object (the endpoint, not the triggers) and has its own inline commit rather than the SaveBar.
    const savedOverrides = useMemo(() => overridesOf(relayEndpoint as WebRelayClientEndpoint | undefined), [relayEndpoint]);
    const [wiringDraft, setWiringDraft] = useState<WiringOverrides>(savedOverrides);
    const [wiringBusy, setWiringBusy] = useState(false);
    useEffect(() => setWiringDraft(savedOverrides), [savedOverrides]);

    const wiringDirty = JSON.stringify(wiringDraft) !== JSON.stringify(savedOverrides);
    // What the draft would resolve to, so a bad combination is caught before it is stored.
    const prospectiveWiring = useMemo(
        () => effectiveRelayWiring(defaults, overrideFieldsOf(wiringDraft)),
        [defaults, wiringDraft],
    );
    const wiringValid = prospectiveWiring.horn.number !== prospectiveWiring.lights.number;

    const saveWiring = useCallback(async () => {
        if (!relayEndpoint) return;
        setWiringBusy(true);
        try {
            const next = { ...(relayEndpoint as WebRelayClientEndpoint), ...overrideFieldsOf(wiringDraft) };
            await ClientEndpoints.update(next as unknown as ClientEndpoint);

            /*
             * The triggers' relay entries store raw terminal numbers, so when the effective
             * terminals moved they must be renumbered too — otherwise "fixed the swapped wiring"
             * leaves every alert firing the terminal it always fired, and nothing was fixed.
             */
            const newWiring = effectiveRelayWiring(defaults, next);
            let rewired = 0;
            if (newWiring.horn.number !== wiring.horn.number || newWiring.lights.number !== wiring.lights.number) {
                for (const trigger of triggers ?? []) {
                    if (rewireRelayNumbers(trigger, wiring, newWiring)) {
                        await EventTriggers.update(trigger);
                        rewired += 1;
                    }
                }
            }

            // The endpoints effect only refetches on a device change, so fold the save in locally.
            setEndpoints((prev) => prev?.map((e) => (e.id === next.id ? (next as unknown as ClientEndpoint) : e)));
            toast(
                rewired > 0
                    ? `Wiring saved. Relay entries renumbered on ${rewired} trigger${rewired === 1 ? "" : "s"}.`
                    : "Wiring saved",
            );
            onSaved();
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not save the wiring");
        } finally {
            setWiringBusy(false);
        }
    }, [relayEndpoint, wiringDraft, defaults, wiring, triggers, toast, onSaved]);

    const saved = useMemo(() => readNotificationSettings(byCategory, wiring), [byCategory, wiring]);
    const [draft, setDraft] = useState<NotificationSettings>(saved);
    useEffect(() => setDraft(saved), [saved]);

    const fleet = defaults ? notificationsFromDefaults(defaults) : undefined;

    // Whether an absent email endpoint is repairable from here: provisioning copies the fleet's
    // stated recipients, and with none stated it declines rather than creating a decoy.
    const canProvisionEmail = fleetCanEmail(defaults);

    /*
     * The truck is missing pieces the fleet's stated rules need — a trigger for a category the
     * fleet notifies on, or the email endpoint those notifications go to. Surfaced as its own
     * state rather than as "overridden", because a unit that was never set up is a fault, not a
     * choice — and the remedy (the reset, whose commit now provisions) should say what it fixes.
     *
     * Gated on fleetRunsAlert, which is the same predicate the reset itself applies. Asking only
     * "does the fleet notify on this?" advertised a repair for alerts the fleet has switched off:
     * the reset declined, this list recomputed unchanged, and the banner became one no button could
     * clear — the exact dead end this section exists to remove.
     */
    const missingWanted =
        fleet && defaults && triggers
            ? ALERT_CATEGORIES.filter(
                  (c) => (fleet[c].email || fleet[c].relays !== 0) && fleetRunsAlert(defaults, c) && !byCategory[c],
              )
            : [];
    const fleetWantsEmail = fleet ? ALERT_CATEGORIES.some((c) => fleet[c].email) : false;
    const needsSetup =
        missingWanted.length > 0
            ? `${device.name} is missing the ${missingWanted.map((c) => ALERT_CATEGORY_LABELS[c].toLowerCase()).join(", ")} trigger${
                  missingWanted.length === 1 ? "" : "s"
              } the fleet's notification rules attach to — resetting to the fleet default creates and wires ${
                  missingWanted.length === 1 ? "it" : "them"
              }.`
            : fleetWantsEmail && endpoints && emailEndpoints.length === 0
              ? canProvisionEmail
                  ? `${device.name}'s site has no email endpoint, so its alerts email nobody. Resetting to the fleet default creates one carrying the fleet's notification addresses.`
                  : `${device.name}'s site has no email endpoint, and the fleet has no notification addresses to create one from — set them under Settings → Notifications first.`
              : undefined;

    const inherited = fleet ? notificationsMatch(saved, fleet, canProvisionEmail) && !overridden && !needsSetup : false;
    const differences = fleet
        ? notificationDifferences(saved, fleet, canProvisionEmail).map((c) => ALERT_CATEGORY_LABELS[c])
        : [];

    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

    const commit = useCallback(
        async (next: NotificationSettings, message: string, title: string) => {
            setBusy(true);
            const skipped: string[] = [];
            const created: string[] = [];
            // Why something in `skipped` stayed missing — a declined or failed creation, in words.
            const notes: string[] = [];
            try {
                // The render guard below narrows this, but commit is defined before it.
                const known = endpoints ?? [];
                const wantsEmail = ALERT_CATEGORIES.some((c) => next[c].email);
                const wantsRelays = ALERT_CATEGORIES.some((c) => next[c].relays);

                /*
                 * Everything this commit might do, decided up front so the progress bar has a
                 * length before the first write. Each block below reports against these keys —
                 * provisionMissingAlerts under the category names and the zone step — so the plan
                 * and the work agree by construction rather than by keeping two lists in step.
                 */
                const canProvision = Boolean(facility && defaults);
                const existingRelay = known.find((e) => (e as { $type?: string }).$type?.includes("WebRelay"));
                const needEmailEndpoint = !emailEndpoints[0] && wantsEmail && canProvision;
                const needRelayEndpoint = wantsRelays && canProvision && !existingRelay;
                const wantedMissing = ALERT_CATEGORIES.filter((c) => (next[c].email || next[c].relays) && !byCategory[c]);
                const willProvision = wantedMissing.length > 0 && canProvision;
                const drawingZone =
                    willProvision &&
                    facility !== undefined &&
                    defaults !== undefined &&
                    !findProtectedZone(facility) &&
                    wantedMissing.some((c) => c !== "speeding" && fleetRunsAlert(defaults, c));

                const EMAIL_STEP = { key: "email", label: "Creating the email endpoint" };
                const RELAY_STEP = { key: "relay", label: "Creating the relay board endpoint" };
                const RELOAD_STEP = { key: "reload", label: "Reading back the new alerts" };
                const WIRING_STEP = { key: "wiring", label: "Wiring email and relays to the alerts" };

                beginProgress(title, [
                    ...(needEmailEndpoint ? [EMAIL_STEP] : []),
                    ...(needRelayEndpoint ? [RELAY_STEP] : []),
                    ...(drawingZone ? [PROTECTED_ZONE_STEP] : []),
                    ...(willProvision ? wantedMissing.map((c) => ({ key: c, label: provisionStepLabel(c) })) : []),
                    ...(willProvision ? [RELOAD_STEP] : []),
                    WIRING_STEP,
                ]);

                /*
                 * Provision what the site is missing before wiring anything to it. Both endpoints
                 * come from the fleet's stated values — recipients and the relay board's address —
                 * so this creates something that actually works rather than an empty shell. With no
                 * addresses stated, ensureEmailEndpoint declines and the reader is told why: an
                 * endpoint that reaches nobody looks configured while notifying no one.
                 */
                let endpointCreated = false;
                let email = emailEndpoints[0];
                if (needEmailEndpoint && facility && defaults) {
                    reportProgress({ ...EMAIL_STEP, state: "active" });
                    const made = await ensureEmailEndpoint(
                        facility,
                        device.organizationId,
                        defaults.notificationEmailAddresses,
                        known,
                    );
                    if (made) {
                        email = made;
                        endpointCreated = true;
                        created.push("an email endpoint");
                        reportProgress({ ...EMAIL_STEP, state: "done" });
                    } else {
                        reportProgress({
                            ...EMAIL_STEP,
                            state: "skipped",
                            note: "The fleet has no notification addresses to create one from.",
                        });
                    }
                }

                // Held on to, not just created: a trigger that has never had a relay join needs the
                // endpoint itself to build one. Discarding it here is what left "notify by relay"
                // silently doing nothing on any trigger that wasn't already wired.
                let relay = existingRelay;
                if (needRelayEndpoint && facility && defaults) {
                    reportProgress({ ...RELAY_STEP, state: "active" });
                    const made = await ensureRelayEndpoint(
                        facility,
                        device.organizationId,
                        defaults.webRelayIpAddress,
                        known,
                    );
                    if (made) {
                        relay = made;
                        endpointCreated = true;
                        created.push("a relay board endpoint");
                        reportProgress({ ...RELAY_STEP, state: "done" });
                    } else {
                        reportProgress({ ...RELAY_STEP, state: "skipped", note: "No relay board address is set for the fleet." });
                    }
                }

                /*
                 * Then the triggers themselves. A category the target wants heard, on a truck with
                 * no trigger of that kind at all, used to be skipped with "no trigger for it yet" —
                 * which left the units that were never onboarded properly permanently unfixable
                 * from here, exactly the trucks a reset exists for. So they are created at the
                 * fleet's stated thresholds (see provisionMissingAlerts), then wired below like any
                 * other. Categories governed by a fleet-wide trigger are not in this list — those
                 * are byCategory hits — and stay read-only here, because rewiring one would change
                 * what every truck it governs does.
                 */
                const createdCategories = new Set<AlertCategory>();
                let effectiveTriggers = triggers ?? [];
                if (willProvision && facility && defaults) {
                    const made = await provisionMissingAlerts({
                        facility,
                        organizationId: device.organizationId,
                        defaults,
                        categories: wantedMissing,
                        onProgress: reportProgress,
                    });
                    made.created.forEach((c) => createdCategories.add(c));
                    created.push(...made.created.map((c) => `the ${ALERT_CATEGORY_LABELS[c]} alert`));
                    notes.push(...made.notes);

                    // Re-read rather than hand-assembling: the ensure* helpers report what they
                    // created, not the rows — and the wiring below needs real ids and joins.
                    if (made.created.length > 0) {
                        reportProgress({ ...RELOAD_STEP, state: "active" });
                        effectiveTriggers =
                            (await EventTriggers.getByFacilityId(device.organizationId, device.facilityId).catch(
                                () => triggers ?? [],
                            )) ?? [];
                        reportProgress({ ...RELOAD_STEP, state: "done" });
                    } else {
                        reportProgress({ ...RELOAD_STEP, state: "skipped", note: "Nothing new was created, so there is nothing to read back." });
                    }
                } else if (wantedMissing.length > 0) {
                    notes.push("This unit's site could not be loaded, so its missing triggers were not created — try again.");
                }

                reportProgress({ ...WIRING_STEP, state: "active" });

                // Only this truck's own triggers are writable — see triggersForFacility.
                const ownByCategory = triggersByCategory(triggersForFacility(effectiveTriggers, device.facilityId));

                for (const category of ALERT_CATEGORIES) {
                    const trigger = ownByCategory[category];
                    if (!trigger) {
                        // Only worth mentioning when the reader asked for something to happen.
                        if (next[category].email || next[category].relays) {
                            skipped.push(
                                byCategory[category]
                                    ? `${ALERT_CATEGORY_LABELS[category]} (fleet-wide, edit under Integrations)`
                                    : ALERT_CATEGORY_LABELS[category],
                            );
                        }
                        continue;
                    }

                    let touched = applyRelaySelection(trigger, next[category].relays, relay, wiring);
                    // Same repairs every other save carries: a target filter the device can match and
                    // relay entries it will evaluate — see repairTrigger.
                    touched = repairTrigger(trigger) || touched;

                    // A trigger created moments ago has no pulse configuration on its fresh relay
                    // entries; the wizard gives a new truck the fleet's pulse seconds, and a repair
                    // should land in the same place. Existing triggers keep theirs — pulses are the
                    // Alerts & Relays tab's fields once a truck is set up.
                    if (createdCategories.has(category) && defaults) {
                        touched = setRelayPulseSeconds(trigger, wiring.lights.number, defaults.lightsPulseSeconds) || touched;
                        touched = setRelayPulseSeconds(trigger, wiring.horn.number, defaults.hornPulseSeconds) || touched;
                    }

                    if (next[category].relays && !relay) {
                        skipped.push(`${ALERT_CATEGORY_LABELS[category]} relays`);
                    }

                    if (email) {
                        touched = applyEmailNotification(trigger, email, next[category].email) || touched;
                    } else if (next[category].email) {
                        skipped.push(`${ALERT_CATEGORY_LABELS[category]} email`);
                    }

                    if (touched) await EventTriggers.update(trigger);
                }
                reportProgress({ ...WIRING_STEP, state: "done" });

                // The endpoints effect only refetches when the device changes, so an endpoint
                // created just now would stay invisible — the "no email endpoint" card and the
                // "Not set up" row would keep saying so until the page was reopened.
                if (endpointCreated) {
                    const fresh = await ClientEndpoints.getAll(device.organizationId, device.facilityId).catch(
                        () => undefined,
                    );
                    if (fresh) setEndpoints(fresh);
                }

                setOverridden(false);
                endProgress();
                const listOf = (items: string[]) =>
                    items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
                const madeNote = created.length > 0 ? ` Created ${listOf(created)} for this site.` : "";
                const whyNot = notes.length > 0 ? ` ${notes.join(" ")}` : "";
                toast(
                    skipped.length > 0
                        ? `Saved.${madeNote} ${skipped.join(", ")} could not be set — this site has no trigger or endpoint for it yet.${whyNot}`
                        : `${message}.${madeNote}${whyNot}`.replace(/\.\s*$/, ""),
                );
                onSaved();
            } catch (err) {
                // The dialog stays up with the broken step marked and the message beneath it — the
                // one moment a director needs to read rather than be told, and a toast would vanish
                // under the modal anyway.
                failProgress(err instanceof Error ? err.message : "Could not save that");
            } finally {
                setBusy(false);
            }
        },
        [
            byCategory,
            emailEndpoints,
            endpoints,
            facility,
            defaults,
            device.organizationId,
            device.facilityId,
            triggers,
            toast,
            onSaved,
            wiring,
            beginProgress,
            reportProgress,
            failProgress,
            endProgress,
        ],
    );

    if (!triggers || !endpoints) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    const recipients = emailEndpoints.flatMap((e) =>
        endpointRecipients(e).map((address) => ({ address, endpointName: e.endpointName ?? "Email endpoint" })),
    );

    return (
        <>
            <InheritedSection
                title="Notifications"
                inherited={inherited}
                onOverride={() => setOverridden(true)}
                onResetToDefault={
                    fleet && !inherited
                        ? () =>
                              commit(
                                  fleet,
                                  `${device.name} is back on the fleet default`,
                                  `Resetting ${device.name} to the fleet default`,
                              )
                        : undefined
                }
                differences={differences}
                needsSetup={needsSetup}
            >
                <Card>
                    <CardHead title="Notification rules" note="per alert, for this unit" />
                    <div className="mt-2">
                        {ALERT_CATEGORIES.map((category) => {
                            const missing = !byCategory[category];
                            // Read off the same list the banner and the reset use, rather than
                            // restating the predicate — a second copy is how the two drifted apart.
                            const repairable = missingWanted.includes(category);
                            return (
                                <FRow
                                    stack
                                    key={category}
                                    title={ALERT_CATEGORY_LABELS[category]}
                                    sub={
                                        missing
                                            ? repairable
                                                ? "No trigger of this kind on this unit yet — Reset to fleet default creates it"
                                                : "No trigger of this kind on this unit yet"
                                            : "Email the recipients, and close these relays"
                                    }
                                >
                                    <span className="flex items-center gap-2">
                                        {/* Named controls: an unlabelled switch beside an alert's name reads as
                                            "is this alert on", which is the Alerts & Relays tab's question, not
                                            this one's. */}
                                        <span className="text-[12px] text-[var(--muted)]">Relays</span>
                                        <Select
                                            value={String(draft[category].relays)}
                                            disabled={missing}
                                            aria-label={`${ALERT_CATEGORY_LABELS[category]} relays`}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    [category]: { ...draft[category], relays: Number(e.target.value) as RelaySelection },
                                                })
                                            }
                                        >
                                            {relayChoices(wiring).map((c) => (
                                                <option key={c.label} value={String(c.value)}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </Select>
                                        <span className="pl-1 text-[12px] text-[var(--muted)]">Email</span>
                                        {/* Enabled without an endpoint when saving can create one — see commit.
                                            Still off when the fleet has no addresses to build it from. */}
                                        <Toggle
                                            on={draft[category].email}
                                            disabled={missing || (emailEndpoints.length === 0 && !canProvisionEmail)}
                                            onChange={(v) => setDraft({ ...draft, [category]: { ...draft[category], email: v } })}
                                            label={`Email on ${ALERT_CATEGORY_LABELS[category]}`}
                                        />
                                    </span>
                                </FRow>
                            );
                        })}
                    </div>
                    <CardNote>
                        These rules govern who <b className="font-semibold text-[var(--text)]">hears about</b> an alert — not
                        whether it fires. Switching an alert itself on or off lives under{" "}
                        <b className="font-semibold text-[var(--text)]">Alerts &amp; Relays</b>, so its email can be off here
                        while the alert still fires on the truck. The relay picker rewrites configuration that the unit itself
                        downloads and parses, so an entry someone set by hand — its expression, state or pulse — is preserved
                        when a terminal is added or dropped.
                    </CardNote>
                </Card>

                <Card>
                    <CardHead title="Who gets notified" note="email endpoints on this unit's site" />
                    {emailEndpoints.length === 0 ? (
                        <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">
                            {canProvisionEmail
                                ? "This site has no email endpoint yet, so its alerts email nobody. Switching email on for any alert — or resetting to the fleet default — creates one carrying the fleet's notification addresses."
                                : "This site has no email endpoint, and the fleet has no notification addresses to create one from. Set them under Settings → Notifications, then switch email on here."}
                        </p>
                    ) : recipients.length === 0 ? (
                        <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">
                            {emailEndpoints.length === 1 ? "An email endpoint exists" : `${emailEndpoints.length} email endpoints exist`} for
                            this site, but no addresses are set on {emailEndpoints.length === 1 ? "it" : "them"}.
                        </p>
                    ) : (
                        <div className="mt-2">
                            {recipients.map((r) => (
                                <FRow key={`${r.endpointName}-${r.address}`} title={r.address} sub={r.endpointName}>
                                    <Tag tone="safe">Notified</Tag>
                                </FRow>
                            ))}
                        </div>
                    )}
                    <CardNote>
                        Addresses live on the endpoint itself and are edited on the main dashboard. Drivers cannot change this
                        list from the cab.
                    </CardNote>
                </Card>

                {!device.facilityId && <Note tone="crit">This unit has no site, so it has no endpoints to notify.</Note>}
            </InheritedSection>

            {relayEndpoint && (
                <Card>
                    <CardHead title="Relay wiring" note="this truck only — blank follows the fleet" />
                    <div className="mt-2">
                        {(["horn", "lights"] as const).map((slot) => {
                            const fleetSlot = effectiveRelayWiring(defaults)[slot];
                            const role = slot === "horn" ? "Horn" : "Lights";
                            return (
                                <FRow
                                    stack
                                    key={slot}
                                    title={`${role} slot`}
                                    sub={`Fleet: relay ${fleetSlot.number} · “${fleetSlot.name}” · ${fleetSlot.snoozable ? "snoozable" : "not snoozable"}`}
                                >
                                    <span className="flex items-center gap-2">
                                        <TextInput
                                            value={wiringDraft[slot].name}
                                            onChange={(e) =>
                                                setWiringDraft({
                                                    ...wiringDraft,
                                                    [slot]: { ...wiringDraft[slot], name: e.target.value },
                                                })
                                            }
                                            className="w-[120px]"
                                            placeholder={fleetSlot.name}
                                            aria-label={`${role} slot name on this truck`}
                                        />
                                        <Select
                                            value={wiringDraft[slot].number}
                                            aria-label={`${role} slot relay number on this truck`}
                                            onChange={(e) =>
                                                setWiringDraft({
                                                    ...wiringDraft,
                                                    [slot]: { ...wiringDraft[slot], number: e.target.value },
                                                })
                                            }
                                        >
                                            <option value="">Fleet (relay {fleetSlot.number})</option>
                                            {[1, 2, 3, 4].map((n) => (
                                                <option key={n} value={String(n)}>
                                                    Relay {n}
                                                </option>
                                            ))}
                                        </Select>
                                        <Select
                                            value={wiringDraft[slot].snoozable}
                                            aria-label={`${role} slot snoozable on this truck`}
                                            onChange={(e) =>
                                                setWiringDraft({
                                                    ...wiringDraft,
                                                    [slot]: {
                                                        ...wiringDraft[slot],
                                                        snoozable: e.target.value as "" | "yes" | "no",
                                                    },
                                                })
                                            }
                                        >
                                            <option value="">Fleet ({fleetSlot.snoozable ? "snoozable" : "no snooze"})</option>
                                            <option value="yes">Snoozable</option>
                                            <option value="no">No snooze</option>
                                        </Select>
                                    </span>
                                </FRow>
                            );
                        })}
                    </div>
                    {!wiringValid && (
                        <div className="px-5 pb-3">
                            <Note tone="crit">
                                Horn and lights resolve to the same terminal (relay {prospectiveWiring.horn.number}) — change one.
                            </Note>
                        </div>
                    )}
                    {wiringDirty && (
                        <div className="flex items-center justify-end gap-2 px-5 pb-4">
                            <Btn tone="danger" onClick={() => setWiringDraft(savedOverrides)} disabled={wiringBusy}>
                                Discard
                            </Btn>
                            <Btn onClick={saveWiring} disabled={wiringBusy || !wiringValid}>
                                {wiringBusy ? "Saving…" : "Save wiring"}
                            </Btn>
                        </div>
                    )}
                    <CardNote>
                        For a truck wired differently from the standard build — swapped terminals, or other gear on a slot
                        (a vibrating vest where the beacons usually go). Names show in the cab; snoozable decides whether the
                        driver gets a snooze button for it. Saving renumbers this truck's relay entries to match.
                    </CardNote>
                </Card>
            )}

            <ProgressDialog run={progressRun} onClose={endProgress} />

            <SaveBar
                show={dirty && !inherited}
                busy={busy}
                label={`Unsaved · ${device.name}`}
                onSave={() => commit(draft, `Notifications saved to ${device.name}`, `Saving ${device.name}'s notifications`)}
                onDiscard={() => setDraft(saved)}
            />
        </>
    );
};


